import { useState } from "react";
import { EmailDeliveryLogs } from "./EmailDeliveryLogs";
import type { MailInput, AccountScope } from "@proof/accounts";
import { Select, Button, Control } from "./components";
import { CheckCircle2, CircleAlert, KeyRound } from "lucide-react";
import { describeSyntaxProblem, parseJson } from "@proof/authoring/json-import";
import {
  accountRequest,
  googleStart,
  type AdministrationView,
} from "./account-api";
const providers = [
  ["sparkpost", "SparkPost"],
  ["bird", "Bird.com"],
  ["gmail-oauth", "Gmail · sign in with Google"],
  ["gmail-service", "Gmail Workspace · service-account key"],
  ["gmail-password", "Gmail · app password"],
] as const;
export function AccountEmail({
  administration,
  run,
  refresh,
  setup = false,
}: {
  administration: AdministrationView;
  run: (work: () => Promise<unknown>, success?: string) => Promise<void>;
  refresh: () => Promise<void>;
  setup?: boolean;
}) {
  const [logRefresh, setLogRefresh] = useState(0);
  const [scope, setScope] = useState(() =>
      administration.scopes.some((scope) => scope.kind === "global")
        ? "global"
        : administration.scopes.find((scope) => scope.kind === "site")?.id ||
          "global",
    ),
    [editing, setEditing] = useState<string>(),
    [provider, setProvider] = useState<MailInput["provider"]>("sparkpost"),
    [name, setName] = useState(setup ? "SparkPost" : ""),
    [from, setFrom] = useState(""),
    [fromName, setFromName] = useState("Proof Studio"),
    [region, setRegion] = useState<"us" | "eu">("us"),
    [credentials, setCredentials] = useState<Record<string, string>>({});
  const scopes = administration.scopes.filter(
      (scope) => scope.kind !== "company",
    ),
    chosen: AccountScope =
      scope === "global" ? { kind: "global" } : { kind: "site", id: scope };
  const scopeName = (value: AccountScope) =>
    value.kind === "global"
      ? "Application default"
      : value.kind === "site"
        ? administration.sites.find((site) => site.id === value.id)?.name ||
          value.id
        : "Company";
  const links = administration.links;
  if (!scopes.length)
    return (
      <section>
        <h2>Email providers</h2>
        <p>
          Your site or application administrator manages email delivery for this
          company.
        </p>
      </section>
    );
  const secret = (key: string, label: string, multiline = false) => (
    <Control label={label}>
      {multiline ? (
        <textarea
          aria-label={label}
          value={credentials[key] || ""}
          rows={5}
          autoComplete="off"
          onChange={(event) =>
            setCredentials({ ...credentials, [key]: event.target.value })
          }
        />
      ) : (
        <input
          aria-label={label}
          type="password"
          required={
            key === "apiKey" &&
            (provider === "sparkpost" || provider === "bird") &&
            !editing
          }
          autoComplete="new-password"
          value={credentials[key] || ""}
          onChange={(event) =>
            setCredentials({ ...credentials, [key]: event.target.value })
          }
        />
      )}
    </Control>
  );
  return (
    <section aria-label="Email provider configuration">
      <h2>Email providers</h2>
      {setup && (
        <>
          <p>
            Choose how Proof Studio sends invitations, password resets and
            verification codes. Use SparkPost with a transmission API key, or
            connect a Gmail mailbox.
          </p>
          <div className="button-row" aria-label="Choose setup email provider">
            {providers.map(([id, label]) => (
              <Button
                key={id}
                kind={provider === id ? "primary" : undefined}
                disabled={!!editing}
                onClick={() => {
                  setProvider(id);
                  setName(label);
                  setCredentials({});
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </>
      )}
      <p>
        Sites inherit the application email provider unless a site connection is
        assigned. Secrets are stored privately; existing credentials are
        retained when their inputs are left blank.
      </p>
      {administration.mail.length > 0 && (
        <div className="account-table">
          <table>
            <thead>
              <tr>
                <th>Connection</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {administration.mail.map((mail) => (
                <tr key={mail.id}>
                  <td>
                    <strong>{mail.name}</strong>
                    <small>
                      {mail.from} · {mail.provider}
                    </small>
                  </td>
                  <td>{scopeName(mail.scope)}</td>
                  <td>
                    {!mail.ready
                      ? "Google consent needed"
                      : mail.validatedRevision === mail.revision
                        ? "Validated by test"
                        : "Configured · test required for email-link sign-in"}
                  </td>
                  <td>
                    <div className="button-row">
                      <Button
                        onClick={() => {
                          setEditing(mail.id);
                          setScope(
                            mail.scope.kind === "site"
                              ? mail.scope.id
                              : "global",
                          );
                          setProvider(mail.provider);
                          setName(mail.name);
                          setFrom(mail.from);
                          setFromName(mail.fromName);
                          setRegion(mail.region);
                          setCredentials({});
                        }}
                      >
                        Edit connection
                      </Button>
                      {mail.provider === "gmail-oauth" && (
                        <Button
                          onClick={() =>
                            void run(() => googleStart("mail", mail.id))
                          }
                        >
                          Connect Google mailbox
                        </Button>
                      )}
                      <Button
                        disabled={!mail.ready}
                        onClick={() =>
                          void run(async () => {
                            try {
                              return await accountRequest("/email/test", {
                                id: mail.id,
                              });
                            } finally {
                              setLogRefresh((n) => n + 1);
                            }
                          }, "Provider accepted the test email. See Email delivery logs for details.")
                        }
                      >
                        Send test email
                      </Button>
                      <Button
                        onClick={() =>
                          void run(async () => {
                            await accountRequest("/email/assignment", {
                              scope: mail.scope,
                              mailId: mail.id,
                            });
                            await refresh();
                          }, "Email provider assigned.")
                        }
                      >
                        Use for {scopeName(mail.scope)}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EmailDeliveryLogs
        refreshKey={logRefresh}
        connections={administration.mail}
      />
      {!setup &&
        administration.scopes
          .filter((scope) => scope.kind === "site")
          .map((site) => (
            <div
              className="button-row"
              key={site.kind === "site" ? site.id : ""}
            >
              <span>{scopeName(site)}</span>
              <Button
                onClick={() =>
                  void run(async () => {
                    await accountRequest("/email/assignment", {
                      scope: site,
                      mailId: null,
                    });
                    await refresh();
                  }, "Site now inherits the application email provider.")
                }
              >
                Inherit application email
              </Button>
            </div>
          ))}
      <form
        className="account-form"
        onSubmit={(event) => {
          event.preventDefault();
          void run(async () => {
            const secrets = Object.fromEntries(
              Object.entries(credentials).filter(([, value]) => value.length),
            );
            await accountRequest("/email", {
              ...(editing ? { id: editing } : {}),
              name,
              scope: chosen,
              provider,
              from,
              fromName,
              region,
              ...secrets,
            });
            setCredentials({});
            setEditing(undefined);
            await refresh();
          }, "Email connection saved. Send a test message to check delivery.");
        }}
      >
        <h3>{editing ? "Edit email connection" : "Add email connection"}</h3>
        <div className="control-grid">
          <Control label="Email configuration scope">
            <Select
              disabled={!!editing || setup}
              value={
                scopes.some((value) => value.kind === "global")
                  ? scope
                  : scope === "global" && scopes[0]?.kind === "site"
                    ? scopes[0].id
                    : scope
              }
              onChange={(event) => setScope(event.target.value)}
            >
              {scopes.map((value) => (
                <option
                  key={scopeName(value)}
                  value={value.kind === "site" ? value.id : "global"}
                >
                  {scopeName(value)}
                </option>
              ))}
            </Select>
          </Control>
          <Control label="Email provider">
            <Select
              disabled={!!editing}
              value={provider}
              onChange={(event) => {
                setProvider(event.target.value as typeof provider);
                if (setup)
                  setName(
                    providers.find(([id]) => id === event.target.value)?.[1] ||
                      "",
                  );
                setCredentials({});
              }}
            >
              {providers.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </Control>
          <Control label="Connection name">
            <input
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Control>
          <Control label="Sender display name">
            <input
              required
              value={fromName}
              onChange={(event) => setFromName(event.target.value)}
            />
          </Control>
          <Control label="Sender email">
            <input
              type="email"
              required
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Control>
          {provider === "bird" && (
            <>
              <Control label="Bird region">
                <Select
                  value={region}
                  onChange={(e) => setRegion(e.target.value as typeof region)}
                >
                  <option value="us">US</option>
                  <option value="eu">EU</option>
                </Select>
              </Control>
              {secret("apiKey", "Bird API key")}
              <p>
                Use a regional Bird API key with email sending permission and a
                verified sender.{" "}
                <a
                  href="https://bird.com/docs/get-started/send-your-first-email"
                  target="_blank"
                  rel="noreferrer"
                >
                  Bird setup guide
                </a>
              </p>
            </>
          )}
          {provider === "sparkpost" && (
            <>
              <Control label="SparkPost region">
                <Select
                  value={region}
                  onChange={(event) =>
                    setRegion(event.target.value as typeof region)
                  }
                >
                  <option value="us">US</option>
                  <option value="eu">EU</option>
                </Select>
              </Control>
              {secret("apiKey", "SparkPost API key")}
              <p>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={
                    region === "eu"
                      ? links.sparkpostEUKeys
                      : links.sparkpostKeys
                  }
                >
                  Create a SparkPost API key
                </a>
                . Enable Transmissions access and verify your sender domain.
              </p>
            </>
          )}
          {provider === "gmail-oauth" && (
            <>
              <Control label="Google OAuth client ID">
                <input
                  autoComplete="off"
                  value={credentials.clientId || ""}
                  onChange={(event) =>
                    setCredentials({
                      ...credentials,
                      clientId: event.target.value,
                    })
                  }
                />
              </Control>
              {secret("clientSecret", "Google OAuth client secret")}
              <p>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={links.googleCredentials}
                >
                  Create Google OAuth credentials
                </a>{" "}
                and{" "}
                <a target="_blank" rel="noreferrer" href={links.gmailEnable}>
                  enable the Gmail API
                </a>
                . Register{" "}
                <code>
                  {administration.publicOrigin}/api/accounts/google/callback
                </code>{" "}
                as the redirect URI. Save, then connect the sender's Google
                account. A Workspace administrator can authorize their mailbox;
                organization-wide sending uses a delegated service-account key.
              </p>
            </>
          )}
          {provider === "gmail-service" && (
            <>
              <ServiceAccountKeyField
                value={credentials.serviceAccount || ""}
                onChange={(text) =>
                  setCredentials({ ...credentials, serviceAccount: text })
                }
              />
              <p>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={links.googleServiceKeys}
                >
                  Create a service-account key
                </a>
                ,{" "}
                <a target="_blank" rel="noreferrer" href={links.gmailEnable}>
                  enable Gmail
                </a>
                , and authorize its client ID with{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={links.workspaceDelegation}
                >
                  Workspace domain-wide delegation
                </a>{" "}
                for <code>https://www.googleapis.com/auth/gmail.send</code>. The
                sender email is the delegated mailbox.
              </p>
            </>
          )}
          {provider === "gmail-password" && (
            <>
              {secret("appPassword", "Gmail app password")}
              <p>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={links.gmailAppPasswords}
                >
                  Create a Gmail app password
                </a>{" "}
                for the sender mailbox. Google two-step verification and your
                Workspace policy must permit app passwords.
              </p>
            </>
          )}
        </div>
        <p className="muted">
          {provider === "bird"
            ? "Save, test and assign this Bird connection to enable application email. Test details appear in Email delivery logs."
            : provider === "sparkpost"
              ? "SparkPost requires a transmission API key and a verified sender domain. Save this connection to use it for application email."
              : "Gmail sending requires OAuth, a service-account key or an app password. A regular Google API key cannot send mail."}
        </p>
        <div className="button-row">
          <Button kind="primary" type="submit">
            Save email connection
          </Button>
          {editing && (
            <Button
              onClick={() => {
                setEditing(undefined);
                setCredentials({});
                setName("");
                setFrom("");
              }}
            >
              Cancel editing
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}

/** Reads what a Google service-account key says about itself; never the secret. */
export function inspectServiceAccountKey(
  text: string,
):
  | { ok: true; email: string; project?: string; keyId?: string }
  | { ok: false; message: string } {
  const parsed = parseJson(text);
  if (!parsed.ok)
    return {
      ok: false,
      message: `This isn't valid JSON. ${describeSyntaxProblem(parsed.problem)}`,
    };
  const key = parsed.value as Record<string, unknown>;
  if (!key || typeof key !== "object" || Array.isArray(key))
    return { ok: false, message: "A service-account key is a JSON object." };
  if (key.type && key.type !== "service_account")
    return {
      ok: false,
      message:
        key.type === "authorized_user" || key.installed || key.web
          ? "This is an OAuth client or user credential, not a service-account key. In Google Cloud open IAM › Service accounts › Keys › Add key › JSON."
          : `This key's type is "${String(key.type)}". Use a service-account key.`,
    };
  if (typeof key.client_email !== "string" || !key.client_email.includes("@"))
    return {
      ok: false,
      message:
        'The key is missing "client_email". Use the complete downloaded file.',
    };
  if (
    typeof key.private_key !== "string" ||
    !/-----BEGIN [A-Z ]*PRIVATE KEY-----/.test(key.private_key)
  )
    return {
      ok: false,
      message:
        'The key is missing its "private_key". Use the complete downloaded file.',
    };
  return {
    ok: true,
    email: key.client_email,
    project: typeof key.project_id === "string" ? key.project_id : undefined,
    keyId:
      typeof key.private_key_id === "string"
        ? key.private_key_id.slice(0, 8)
        : undefined,
  };
}

function ServiceAccountKeyField({
  value,
  onChange,
}: {
  value: string;
  onChange: (text: string) => void;
}) {
  const label = "Google service-account JSON key";
  const [over, setOver] = useState(false),
    [paste, setPaste] = useState(false),
    [fileName, setFileName] = useState("");
  const info = value.trim() ? inspectServiceAccountKey(value) : undefined;
  const read = async (file: File) => {
    if (file.size > 64_000) {
      setFileName(file.name);
      onChange("");
      return;
    }
    setFileName(file.name);
    onChange(await file.text());
  };
  return (
    <div className="control">
      <span className="control-label">
        <span>{label}</span>
        <button
          type="button"
          className="text-button"
          onClick={() => setPaste(!paste)}
        >
          {paste ? "Upload the file instead" : "Paste the key instead"}
        </button>
      </span>
      {paste ? (
        <textarea
          aria-label={label}
          value={value}
          rows={5}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={info && !info.ok ? true : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <label
          className="ps-dropzone"
          style={{ minHeight: 120 }}
          data-over={over ? "" : undefined}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            const file = e.dataTransfer.files[0];
            if (file) void read(file);
          }}
        >
          <KeyRound size={24} aria-hidden />
          <strong>
            {info?.ok
              ? "Choose a different key file"
              : "Drop the downloaded key file here"}
          </strong>
          <span>or click to choose the .json key from your computer</span>
          {fileName && <span className="ps-dropzone-file">{fileName}</span>}
          <input
            type="file"
            className="sr-only"
            accept=".json,application/json"
            aria-label={`${label} file`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void read(file);
            }}
          />
        </label>
      )}
      {info?.ok && (
        <div className="ps-notice" data-tone="success" role="status">
          <CheckCircle2 size={16} aria-hidden />
          <p>
            Key for <strong>{info.email}</strong>
            {info.project && <> in project {info.project}</>}
            {info.keyId && <> (key {info.keyId}…)</>}. It's stored privately
            when you save.
          </p>
        </div>
      )}
      {info && !info.ok && (
        <div className="ps-notice" data-tone="danger" role="alert">
          <CircleAlert size={16} aria-hidden />
          <p>{info.message}</p>
        </div>
      )}
      {!value.trim() && fileName && (
        <div className="ps-notice" data-tone="danger" role="alert">
          <CircleAlert size={16} aria-hidden />
          <p>{fileName} is too large to be a service-account key.</p>
        </div>
      )}
    </div>
  );
}
